import { env } from '@/config/env';

// On the browser we always go through Next's own `/api/v1/*` proxy
// (see `src/app/api/v1/[...path]/route.ts`). This keeps the request
// same-origin so:
//   - cookies/credentials ride along without a third-party CORS allow-list,
//   - the strict CSP `connect-src 'self'` already permits it,
//   - Lighthouse stops reporting `errors-in-console` for the cross-origin
//     XHRs the upstream rejects with `No 'Access-Control-Allow-Origin' header`.
//
// On the server we still call the absolute upstream URL directly — no CORS
// applies to server-side fetches and the proxy would just add a hop.
export const API_CONFIG = {
  baseURL:
    typeof window !== 'undefined'
      ? '/api/v1'
      : env.apiBaseUrl.startsWith('/')
        ? `${env.userAppUrl.replace(/\/$/, '')}${env.apiBaseUrl}`
        : env.apiBaseUrl,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'x-access-key': env.xAccessKey,
  },
} as const;

export const API_ENDPOINTS = {
  auth: {
    login: `/auth/login`,
    logout: `/auth/logout`,
    refresh: `/auth/refresh`,
    forgotPassword: `/auth/forgot-password`,
    resetPassword: `/auth/reset-password`,
    verifyResetToken: (token: string) => `/auth/verify-reset-token/${token}`,
  },
  generalEnquiry: {
    list: '/general-enquiries',
    create: '/general-enquiries',
    export: '/general-enquiries/export',
    verifyRecaptcha: '/general-enquiries/verify-recaptcha',
  },
  enquiries: {
    reveal: '/enquiries/reveal',
  },
  testimonials: {
    list: '/testimonials',
    create: '/admin/testimonials',
    reorder: '/admin/testimonials/reorder',
    update: (id: number | string) => `/admin/testimonials/${id}`,
    updateStatus: (id: number | string) => `/admin/testimonials/${id}/status`,
    delete: (id: number | string) => `/admin/testimonials/${id}`,
  },
  aboutUs: {
    public: '/about-us',
    get: '/admin/about-us',
    update: (id: number) => `/admin/about-us/${id}`,
  },
  commercialManagement: {
    get: '/commercial-management',
    update: '/admin/commercial-management',
  },
  suburbs: {
    list: '/suburbs',
    create: '/suburbs',
    get: (id: number | string) => `/suburbs/${id}`,
    getBySlug: (slug: string) => `/suburbs/slug/${slug}`,
    reorder: '/suburbs/admin/reorder',
    update: (id: number | string) => `/suburbs/${id}`,
    updateStatus: (id: number | string) => `/suburbs/${id}/status`,
    delete: (id: number | string) => `/suburbs/${id}`,
  },
  glossary: {
    public: '/glossary',
    get: '/admin/glossary',
    update: (id: number) => `/admin/glossary/${id}`,
  },
  terms: {
    // public: '/static-pages',
    get: (slug: string) => `/static-pages/slug/${slug}`,
    create: '/static-pages',
    update: (id: number) => `/static-pages/${id}`,
  },
  privacy: {
    get: (slug: string) => `/static-pages/slug/${slug}`,
    create: '/static-pages',
    update: (id: number) => `/static-pages/${id}`,
  },
  faq: {
    get: (slug: string) => `/static-pages/slug/${slug}`,
    create: '/static-pages',
    update: (id: number) => `/static-pages/${id}`,
  },
  footerDescription: {
    get: (slug: string) => `/static-pages/slug/${slug}`,
    create: '/static-pages',
    update: (id: number) => `/static-pages/${id}`,
  },
  marketUpdates: {
    list: '/market-updates',
    create: '/market-updates',
    update: (id: number) => `/market-updates/${id}`,
    publicActive: '/market-updates/active',
  },
  regionalEconomicOverview: {
    get: '/regional-economic-overview',
    update: '/admin/regional-economic-overview',
  },
  media: {
    upload: '/media/upload',
  },
  dashboard: {
    activities: '/admin/activities',
    contentStatus: '/admin/dashboard/content-status',
    enquiryTrend: '/admin/dashboard/enquiry-trend',
    sourceMix: '/admin/dashboard/enquiry-source-mix',
    totals: '/admin/dashboard/totals',
    syncStatus: '/admin/dashboard/sync-status-data',
    triggerSync: '/listings/sync',
    mostUsedFilters: '/admin/dashboard/most-used-filters',
    mostSearchedSuburbs: '/admin/dashboard/most-search-suburbs',
  },
} as const;
